import { BaseFeature } from '../../core/base-feature.js';

/**
 * QuickConfessionFeature - Frontend feature for Quick Confession
 *
 * Sends confession questions to the desktop and receives answers.
 * Maintains a history of all confessions.
 */
export class QuickConfessionFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this.history = [];
        this.isActive = false;
        this.currentQuestion = null;
        this.pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'quick-confession',
            name: 'Quick Confession',
            version: 'v1.0',
            capabilities: ['ask_question', 'dismiss', 'get_state'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[QuickConfession] Initialized');
    }

    /**
     * Ask a question to the desktop subject
     * @param {string} question - The question text
     * @param {Object} options - { minChars, timeoutSeconds }
     * @returns {Promise<Object>} Result
     */
    askQuestion(question, options = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage('ask', {
                question,
                minChars: options.minChars || 1,
                timeoutSeconds: options.timeoutSeconds || 0
            });

            this.pendingRequests.set(messageId, { resolve, reject });
            this.currentQuestion = question;
            this.isActive = true;

            // Timeout after 5s for the request itself (not the confession)
            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    resolve({ success: true, question });
                }
            }, 5000);
        });
    }

    /**
     * Dismiss the active confession modal
     */
    dismiss() {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage('dismiss', {});

            this.pendingRequests.set(messageId, { resolve, reject });

            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    resolve({ success: true });
                }
            }, 5000);
        });
    }

    /**
     * Get current state from desktop
     */
    getState() {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage('get_state', {});

            this.pendingRequests.set(messageId, { resolve, reject });

            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    resolve({ isActive: this.isActive, currentQuestion: this.currentQuestion, history: this.history });
                }
            }, 5000);
        });
    }

    /**
     * Handle incoming messages from desktop
     */
    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'ask_response': {
                const pending = this.pendingRequests.get(payload?.originalMessageId);
                if (pending) {
                    this.pendingRequests.delete(payload.originalMessageId);
                    pending.resolve(payload);
                }
                break;
            }

            case 'dismiss_response': {
                this.isActive = false;
                this.currentQuestion = null;
                const pending = this.pendingRequests.get(payload?.originalMessageId);
                if (pending) {
                    this.pendingRequests.delete(payload.originalMessageId);
                    pending.resolve(payload);
                }
                break;
            }

            case 'get_state_response': {
                const pending = this.pendingRequests.get(payload?.originalMessageId);
                if (pending) {
                    this.pendingRequests.delete(payload.originalMessageId);
                    if (payload.history) {
                        this.history = payload.history;
                    }
                    pending.resolve(payload);
                }
                break;
            }

            case 'answer_received': {
                // Desktop subject answered the confession
                this.isActive = false;
                this.currentQuestion = null;

                if (payload) {
                    this.history.push(payload);
                    if (this.history.length > 50) {
                        this.history.shift();
                    }
                }

                this.emit('answerReceived', payload);
                break;
            }

            default:
                console.warn(`[QuickConfession] Unknown command: ${command}`);
        }
    }

    async cleanup() {
        this.pendingRequests.clear();
        console.log('[QuickConfession] Cleaned up');
    }
}
